import React, { createContext, useContext, useState, useEffect } from 'react';
import { analytics } from '@heycatch/sdk';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to apply language settings
const applyLanguage = (language) => {
  if (language && ['en', 'es', 'pt', 'zh', 'de', 'fr', 'hi', 'ur', 'ar'].includes(language)) {
    i18n.changeLanguage(language);
    // Set RTL for Arabic and Urdu
    if (language === 'ar' || language === 'ur') {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  }
};

// Helper to clear localStorage while preserving language preference
const clearStoragePreserveLang = () => {
  const lang = localStorage.getItem('i18nextLng');
  localStorage.clear();
  sessionStorage.clear();
  if (lang) {
    localStorage.setItem('i18nextLng', lang);
  }
};

export const AuthProvider = ({ children }) => {
  // Restore cached user immediately for instant UI on reload
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('user_data');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Persist user data to localStorage and sync HeyCatch identity
  useEffect(() => {
    if (user) {
      localStorage.setItem('user_data', JSON.stringify(user));
      analytics.setIdentity(user.id || user.email);
      analytics.setPersonProperties({
        email: user.email,
        name: user.name,
        plan: user.subscription?.plan || 'free',
        isAdmin: user.isAdmin || false,
      });
    } else {
      localStorage.removeItem('user_data');
      analytics.resetIdentity();
    }
  }, [user]);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(storedToken);
            // Only apply user's server language if no local preference is set
            const localLang = localStorage.getItem('i18nextLng');
            if (!localLang && userData.language) {
              applyLanguage(userData.language);
            }
          } else if (response.status === 401 || response.status === 403) {
            // Token is explicitly invalid — clear everything
            localStorage.removeItem('token');
            localStorage.removeItem('user_data');
            setToken(null);
            setUser(null);
          }
          // For other HTTP errors (500, 502, etc.), keep cached state
        } catch (error) {
          // Network error — keep cached user/token, don't kick user out
          console.warn('Auth refresh failed (network), using cached session:', error.message);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Heartbeat for online tracking (every 2 minutes)
  useEffect(() => {
    if (!token) return;
    
    const sendHeartbeat = () => {
      fetch(`${API_URL}/api/auth/heartbeat`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(() => {});
    };
    
    sendHeartbeat(); // Send immediately on mount
    const interval = setInterval(sendHeartbeat, 120000); // Every 2 min
    return () => clearInterval(interval);
  }, [token]);

  const register = async (email, password, name, company, role, referral_code) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, company, role, referral_code })
    });

    if (!response.ok) {
      let errorMsg = 'Registration failed';
      try {
        const error = await response.json();
        errorMsg = error.detail || errorMsg;
      } catch {
        errorMsg = await response.text() || errorMsg;
      }
      throw new Error(errorMsg);
    }

    const data = await response.json();
    
    // Clear any previous session data
    clearStoragePreserveLang();
    
    // Set new token
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    analytics.trackEvent('signup_completed', { plan: 'free' });
    return data;
  };

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      let errorMsg = 'Login failed';
      try {
        const error = await response.json();
        errorMsg = error.detail || errorMsg;
      } catch {
        errorMsg = await response.text() || errorMsg;
      }
      throw new Error(errorMsg);
    }

    const data = await response.json();
    
    // Clear any previous session data
    clearStoragePreserveLang();
    
    // Set new token
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);

    // Track login count for onboarding guide
    const count = parseInt(localStorage.getItem('login_count') || '0', 10);
    localStorage.setItem('login_count', String(count + 1));
    
    // Apply user's language preference only if no local preference
    const localLang = localStorage.getItem('i18nextLng');
    if (!localLang && data.user?.language) {
      applyLanguage(data.user.language);
    }
    
    return data;
  };

  const logout = () => {
    // Track session end
    if (token) {
      fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(() => {});
    }
    clearStoragePreserveLang();
    localStorage.removeItem('user_data');
    setToken(null);
    setUser(null);
  };

  const deleteAccount = async () => {
    const response = await fetch(`${API_URL}/api/auth/delete-account`, {
      method: 'DELETE',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' 
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete account');
    }

    // Clear all data and logout
    clearStoragePreserveLang();
    localStorage.removeItem('user_data');
    setToken(null);
    setUser(null);
    
    return await response.json();
  };

  const updateUser = (updatedFields) => {
    setUser(prev => prev ? { ...prev, ...updatedFields } : prev);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    deleteAccount,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
